// filepath: apps/web/src/features/proc/types.ts
//
// FR-UI-PROC-1 — TypeScript wire contracts for the execution-trace and
// failed-instance dashboard surfaces.
//
// Wire shapes mirror the Go server contracts at:
//   - GET  /api/processes/instances?state=failed
//          → FailedInstanceListResponse (internal/obs/handler.go:62-100)
//   - GET  /api/processes/instances/{id}/step-log
//          → []StepLogResponse        (internal/obs/handler.go:104-167)
//   - POST /api/processes/instances/{id}/retry-step
//          → {id, state}              (internal/obs/handler.go:172-218)
//   - POST /api/processes/instances/{id}/cancel
//          → 204 No Content           (internal/obs/handler.go:223-262)
//
// The SPA binds EXCLUSIVELY to the FR-OBS-6 prefix
// (`/api/processes/instances/...`, singular `step-log`). The
// pre-existing FR-BPM-14 prefix (`/api/process-instances/{id}/...`,
// plural `step-logs`) is a parallel set of handlers on the same
// engine — out of scope for this FR per the impact analysis
// (`.copilot/tasks/active/wf-f4fa1b7b-20260729-req-059/02-impact-analysis.md`
// §"Risk Flags §2").
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-1: the SPA renders exactly what the server returns. No
//     client-side filtering of fields; the server has already stripped
//     any field the caller may not read (INV-2 / FR-AUTH-7).
//   - FR-UI-NFR-2: the SPA never parses `process_step_log` payloads
//     as anything other than data. Step logs are rendered as text
//     rows; payload semantics are server-evaluated only.
//   - INV-9: every string field is rendered as a React text node.
//     Zero `dangerouslySetInnerHTML` anywhere downstream of these types.

/**
 * One step-log entry returned by
 * GET /api/processes/instances/{id}/step-log and embedded inline in
 * the failed-instance list response.
 *
 * The `started_at` field is an ISO-8601 timestamp. The `status`
 * field is server-emitted; v1 ships `started`, `completed`,
 * `failed`, `dead_lettered` per FR-BPM-2 step executor semantics.
 *
 * INV-UI-NFR-2 / INV-9: `error_message` is a tenant-authored free
 * string. The renderer never interprets it as code; it is a text
 * node only.
 */
export interface StepLogEntry {
  /** The BPM node identifier this log entry corresponds to. */
  step_node_id: string;
  /**
   * Server-emitted status. Common values: `started`, `completed`,
   * `failed`, `dead_lettered`. The SPA does not branch on unknown
   * values — unknown statuses render verbatim as a text node.
   */
  status: string;
  /** Wall-clock duration in milliseconds. Absent (omitted) while pending. */
  duration_ms?: number;
  /**
   * Error message (free string). Absent on successful step entries.
   * Server-emitted; the SPA renders verbatim (INV-9).
   */
  error_message?: string;
  /** Number of retries attempted for this step. */
  retry_count: number;
  /** ISO-8601 timestamp when the step began executing. */
  started_at: string;
}

/**
 * One failed process instance entry in the
 * GET /api/processes/instances?state=failed response.
 *
 * The full FR-OBS-6 AC-2 entry shape is preserved verbatim so the
 * SPA never drops a field that downstream consumers rely on (the
 * requirement-validation coverage note flags this — see
 * `.copilot/tasks/active/wf-f4fa1b7b-20260729-req-059/01-requirement-validation.md`
 * §"Completeness Issues Found").
 */
export interface FailedInstance {
  /** Process instance UUID as a string. */
  id: string;
  /** Process definition UUID the instance is pinned to. */
  process_definition_id: string;
  /**
   * Pinned process-definition version at instance start (FR-BPM-10).
   * SPA never silently substitutes `'active'` for this number.
   */
  pinned_definition_version: number;
  /**
   * The BPM node id where the instance failed.
   * Empty string when the failure was at the saga boundary rather
   * than a specific node.
   */
  failed_node: string;
  /**
   * The literal failure-reason string from the most recent
   * failure/dead-letter entry. Notable values include
   * `restored_orphan` (FR-TEN-8, Phase 11 — surfaced with explicit
   * UX treatment per AC-4) and free-form server-emitted messages
   * for other failure modes. The SPA renders the raw string
   * verbatim for non-`restored_orphan` values — no translation
   * (the reason is not platform chrome).
   */
  failure_reason: string;
  /**
   * Inline failure trace — the full step log for the instance.
   * Ordered by `started_at ASC` per the server's contract.
   */
  failure_trace: StepLogEntry[];
  /** Number of retry attempts for the most-recent failure. */
  retries_attempted: number;
  /** ISO-8601 instance-created-at timestamp. */
  created_at: string;
  /** ISO-8601 instance-updated-at timestamp. */
  updated_at: string;
}

/**
 * The wire envelope returned by
 * GET /api/processes/instances?state=failed.
 *
 * Mirrors `internal/obs/handler.go:FailedInstanceListResponse`.
 */
export interface FailedInstanceListResponse {
  instances: FailedInstance[];
}

/**
 * The wire envelope returned by
 * GET /api/processes/instances/{id}/step-log.
 *
 * The endpoint returns a top-level JSON array (not an envelope
 * object); the SPA types it as `StepLogEntry[]` directly at the
 * call site.
 */
export type StepLogListResponse = StepLogEntry[];

/**
 * Successful retry-step response envelope.
 *
 * The server returns `{id, state}`; `state` is the post-retry
 * instance state string. The SPA does not branch on it — TanStack
 * Query invalidation + the dashboard's refetch handle the visible
 * state transition.
 */
export interface RetryStepSuccess {
  id: string;
  state: string;
}

/** Wire body for POST /api/processes/instances/{id}/retry-step. */
export interface RetryStepRequest {
  /**
   * Server-side idempotency key (ARCH Rule 3). Generated by the
   * SPA via `crypto.randomUUID()` per the FR-UI-PROM-2 mutation
   * pattern (mirror `useApprovePromotion.ts:25`).
   */
  idempotency_key: string;
}

/** Wire body for POST /api/processes/instances/{id}/cancel. */
export interface CancelInstanceRequest {
  /**
   * Server-side idempotency key (ARCH Rule 3). Same generation
   * rule as `RetryStepRequest.idempotency_key`.
   */
  idempotency_key: string;
}

/**
 * TanStack Query cache-key factory for proc queries.
 *
 * Shape: `["proc", tenantSlug, "list" | "step-log", ...]`
 *
 * INV-1 — `tenantSlug` is the 2nd segment of every emitted key,
 * mirroring `taskKeys.inbox` (apps/web/src/features/tasks/useTasks.ts:21)
 * and `entityListKeys.page` (apps/web/src/features/lists/hooks/useEntityList.ts:60).
 * The cross-tenant isolation test
 * (`apps/web/src/features/proc/cross-tenant.test.tsx`) asserts this
 * shape verbatim.
 */
export const procKeys = {
  all: ["proc"] as const,
  /** Failed-instance list key. */
  failedInstances: (tenantSlug: string) =>
    ["proc", tenantSlug, "list", "failed"] as const,
  /** Per-instance step-log key. */
  stepLog: (tenantSlug: string, instanceId: string) =>
    ["proc", tenantSlug, "step-log", instanceId] as const,
};

/**
 * Literal `restored_orphan` reason code surfaced from FR-TEN-8.
 *
 * The constant lives here (rather than as a magic string in the
 * component) because AC-4 requires the SPA to give this specific
 * reason code explicit UX treatment — comparing against the
 * constant guarantees the renderer cannot drift from the wire
 * contract.
 */
export const RESTORED_ORPHAN_REASON = "restored_orphan" as const;

/**
 * The 5-state renderer error contract (FR-UI-CORE-10) for the
 * failed-instance dashboard. The `loading` and `success` states
 * are the path-through branches in the page render; the four
 * named states below are mapped from `ApiError` via
 * `classifyFailedInstanceError` (FailedInstanceDashboard.tsx).
 */
export type FailedInstanceStatus =
  | "loading"
  | "success"
  | "fetch-failure"
  | "permission-denied"
  | "stale-version"
  | "rate-limit";
